import React, { FC, useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import EditSection from "components/common/ui/EditSection";
import CommonButton from "components/common/ui/CommonButton";
import FoodItemBasicInfoEditSection from "components/food/ui/ingredient/FoodItemBasicInfoEditSection";
import FoodUsageInfo from "components/food/ui/common/FoodUsageInfo";
import SwapFoodUsagesDialog from "components/food/ui/recipeView/SwapFoodUsagesDialog";
import FoodItemNutritionEditSection from "components/food/ui/ingredient/FoodItemNutritionEditSection";
import FoodItemTagEditSection from "components/food/ui/FoodItemTagEditSection";
import FoodSupplierEditSection from "components/food/ui/ingredient/FoodSupplierEditSection";
import type { AxiosError } from "axios";
import {
  FoodItem,
  FoodItemCreate,
  FoodItemNutrition,
  FoodItemSwapUsagesReport,
  FoodItemNutritionCreate,
  FoodItemNutritionUpdate,
  FoodItemSupplierInfo,
  FoodItemSupplierInfoCreate,
  FoodItemSupplierInfoUpdate,
  FoodItemTag,
  FoodItemUpdate,
  IngredientState,
  StorageTemperature,
} from "client/jspPlatformExperiment";
import { useAppDispatch, useAppSelector } from "hooks/useReduxStore";
import jspPlatformApi from "client/portals";
import foodItemDetailSelector from "store/selectors/foodItem/foodItemDetailSelector";
import { actions as foodItemDetailActions } from "store/slices/foodItem/foodItemDetailSlice";
import { usePromptSnackBar } from "components/common/ui/PromptSnackBarProvider";
import { getJspApiErrorMessage, isApiError } from "models/utils/apiUtils";
import dayjs from "dayjs";
import { Box, Grid } from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import FoodItemSenseCheckingSection from "components/food/ui/ingredient/FoodItemSenseCheckingSection";
import { toFoodItemTags, toFoodItemTagsForCreate } from "utils/food/foodTag";
import {
  InfoLabel,
  RightAlignInfoLabelSubHeader,
} from "components/common/ui/InfoLabel";
import { aggregateFoodUpdateInfo } from "utils/food/foodUpdateInfo";

interface IngredientEditFormsProps {
  saveButtonCallback?: VoidFunction;
}

const IngredientEditForms: FC<IngredientEditFormsProps> = ({
  saveButtonCallback,
}) => {
  const promptSnackBar = usePromptSnackBar();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const editingFoodItemData = useAppSelector(
    foodItemDetailSelector.foodItemBasic,
  );
  const editingFoodItemFull = useAppSelector(
    foodItemDetailSelector.foodItemFull,
  );
  const editingFoodTags = useAppSelector(foodItemDetailSelector.foodTags);
  const editingFoodSuppliers = useAppSelector(
    foodItemDetailSelector.foodSuppliers,
  );
  const editingFoodNutritionRecords = useAppSelector(
    foodItemDetailSelector.foodNutritionRecords,
  );
  const usedAsIngredientIn = useAppSelector(
    foodItemDetailSelector.usedAsIngredientIn,
  );
  const usedInMenus = useAppSelector(foodItemDetailSelector.usedInMenus);
  const usedInMenuLoops = useAppSelector(
    foodItemDetailSelector.usedInMenuLoops,
  );
  const { foodId } = useParams();
  const [isActivated, setIsActivated] = useState<boolean>(true);
  const [isTogglingActivation, setIsTogglingActivation] =
    useState<boolean>(false);
  const [isSwapDialogOpen, setIsSwapDialogOpen] = useState<boolean>(false);
  const [isSwapping, setIsSwapping] = useState<boolean>(false);
  const [swapError, setSwapError] = useState<string | null>(null);
  const [swapReport, setSwapReport] = useState<FoodItemSwapUsagesReport | null>(
    null,
  );
  const isUsedElsewhere =
    usedAsIngredientIn.length > 0 ||
    usedInMenus.length > 0 ||
    usedInMenuLoops.length > 0;
  const isToggleDisabled = isTogglingActivation || isUsedElsewhere;

  useEffect(() => {
    const activated = (
      editingFoodItemFull as { is_activated?: boolean | null } | null
    )?.is_activated;
    if (activated !== undefined && activated !== null) {
      setIsActivated(activated);
    }
  }, [editingFoodItemFull]);

  const [foodItem, setFoodItem] = useState<FoodItem>(
    editingFoodItemData ?? {
      food_name: "",
      state: IngredientState.NONE,
      category: "other",
      storage_temp: StorageTemperature.FRIDGE,
      shelf_life: dayjs.duration({ minutes: 1440 }).toISOString(),
      dispense_category: "",
      food_nutrition_records: [],
      id: 0,
    },
  );

  const [nutritionFacts, setNutritionFacts] = useState<FoodItemNutrition>(
    editingFoodNutritionRecords[0] ?? {
      data_source: "",
      serving_size: 100,
      food_id: editingFoodItemData?.id ?? 0,
    },
  );

  const [foodItemTags, setFoodItemTags] = useState<FoodItemTag[]>(
    editingFoodTags.filter((foodItemTag) => !foodItemTag.removed_by_user) ?? [],
  );
  const [supplierInfo, setSupplierInfo] = useState<FoodItemSupplierInfo>(
    editingFoodSuppliers[0] ?? {
      id: 0,
      supplier_id: "",
      product_code: "",
      product_name: "",
      food_id: editingFoodItemData?.id ? editingFoodItemData.id : 0,
      supplier_unit_size: 0,
      supplier_unit_size_uom: "",
    },
  );

  /**
   * Creating action
   */
  const onCreate = useCallback(() => {
    jspPlatformApi()
      .ingredient.postFoodIng({
        body: {
          ingredient: foodItem as FoodItemCreate,
          supplier_info: supplierInfo as FoodItemSupplierInfoCreate,
          nutrition_facts: nutritionFacts as FoodItemNutritionCreate,
          food_item_tags: toFoodItemTagsForCreate(foodItemTags),
        },
      })
      .then(() => {
        promptSnackBar.setPromptProps({
          isOpen: true,
          message: "Ingredient creation succeed.",
          severity: "success",
          autoHeightDuration: 5000,
        });
        if (saveButtonCallback) {
          saveButtonCallback();
        } else {
          navigate(-1);
        }
      })
      .catch((reason: AxiosError) => {
        promptSnackBar.setPromptProps({
          isOpen: true,
          message: `Ingredient creation failed: ${getJspApiErrorMessage(reason)}`,
          severity: "error",
        });
      });
  }, [
    foodItem,
    supplierInfo,
    nutritionFacts,
    foodItemTags,
    promptSnackBar,
    saveButtonCallback,
    navigate,
  ]);

  const handleToggleActivation = useCallback(() => {
    if (!editingFoodItemData || isTogglingActivation) {
      return;
    }
    const nextValue = !isActivated;
    setIsTogglingActivation(true);
    jspPlatformApi()
      .ingredient.patchFoodIngByFoodId({
        path: { food_id: editingFoodItemData.id },
        body: { ingredient: { is_activated: nextValue } },
      })
      .then(() => {
        setIsActivated(nextValue);
        promptSnackBar.setPromptProps({
          isOpen: true,
          message: nextValue
            ? "Ingredient activated"
            : "Ingredient deactivated",
          severity: "success",
          autoHeightDuration: 3000,
        });
      })
      .catch((error: unknown) => {
        const message = isApiError(error)
          ? getJspApiErrorMessage(error)
          : "Failed to update ingredient activation";
        promptSnackBar.setPromptProps({
          isOpen: true,
          message,
          severity: "error",
          autoHeightDuration: 5000,
        });
      })
      .finally(() => {
        setIsTogglingActivation(false);
      });
  }, [editingFoodItemData, isActivated, isTogglingActivation, promptSnackBar]);

  const handleOpenSwapDialog = useCallback(() => {
    setSwapError(null);
    setSwapReport(null);
    setIsSwapDialogOpen(true);
  }, []);

  const handleCancelSwap = useCallback(() => {
    if (!isSwapping) {
      setIsSwapDialogOpen(false);
    }
  }, [isSwapping]);

  const handleConfirmSwap = useCallback(
    (targetFood: FoodItem) => {
      if (!editingFoodItemData || isSwapping) {
        return;
      }
      setIsSwapping(true);
      setSwapError(null);
      jspPlatformApi()
        .foodItem.postFoodItemByFoodIdSwapUsages({
          path: { food_id: editingFoodItemData.id },
          body: { target_food_id: targetFood.id },
        })
        .then((res) => {
          setSwapReport(res.data ?? null);
        })
        .catch((error: unknown) => {
          const message = isApiError(error)
            ? getJspApiErrorMessage(error)
            : "Failed to swap food usages";
          setSwapError(message);
        })
        .finally(() => {
          setIsSwapping(false);
        });
    },
    [editingFoodItemData, isSwapping],
  );

  const handleCloseSwapReport = useCallback(() => {
    setIsSwapDialogOpen(false);
    setSwapReport(null);
    if (editingFoodItemData) {
      // Only refresh usages: refetching the detail flips the page-level
      // loading flag, which unmounts the form and drops unsaved edits.
      dispatch(
        foodItemDetailActions.fetchFoodItemUsages({
          foodId: editingFoodItemData.id,
        }),
      );
    }
  }, [dispatch, editingFoodItemData]);

  /**
   * Handle create/update food item (ingredient) data to the back-end database
   */
  const handleSaveClick = useCallback(() => {
    const editingFoodId: number | undefined = foodId
      ? Number(foodId)
      : undefined;
    if (editingFoodItemData && editingFoodId) {
      // Update
      jspPlatformApi()
        .ingredient.patchFoodIngByFoodId({
          path: { food_id: editingFoodItemData.id },
          body: {
            ingredient: foodItem as FoodItemUpdate,
            supplier_info: supplierInfo as FoodItemSupplierInfoUpdate,
            nutrition_facts: nutritionFacts as FoodItemNutritionUpdate,
            food_item_tags: toFoodItemTags(foodItemTags),
          },
        })
        .then(() => {
          promptSnackBar.setPromptProps({
            isOpen: true,
            message: "Ingredient updating succeed.",
            severity: "success",
            autoHeightDuration: 5000,
          });
          if (saveButtonCallback) {
            saveButtonCallback();
          } else {
            navigate(-1);
          }
        })
        .catch((reason: AxiosError) => {
          promptSnackBar.setPromptProps({
            isOpen: true,
            message: `Ingredient updating failed: ${getJspApiErrorMessage(reason)}`,
            severity: "error",
          });
        });
    } else {
      // Create
      onCreate();
    }
  }, [
    foodId,
    editingFoodItemData,
    foodItem,
    supplierInfo,
    nutritionFacts,
    foodItemTags,
    promptSnackBar,
    saveButtonCallback,
    navigate,
    onCreate,
  ]);

  return (
    <>
      <EditSection>
        <Grid container>
          <Grid item xs={4}>
            <CommonButton text="Save" positive onClick={handleSaveClick} />
          </Grid>
          <Grid item xs={8}>
            <Box display="flex" justifyContent="flex-end">
              <CommonButton text="Save As New" positive onClick={onCreate} />
            </Box>
          </Grid>
        </Grid>
      </EditSection>
      {(() => {
        const updateInfo = aggregateFoodUpdateInfo(
          editingFoodItemFull,
          editingFoodNutritionRecords,
          editingFoodSuppliers,
        );
        return updateInfo.date ? (
          <RightAlignInfoLabelSubHeader>
            <InfoLabel>
              Modified on {dayjs(updateInfo.date).format("ll")}
              {updateInfo.userFullName ? ` by ${updateInfo.userFullName}` : ""}
            </InfoLabel>
          </RightAlignInfoLabelSubHeader>
        ) : null;
      })()}
      <EditSection
        header={
          foodId
            ? `Basic Information (Food ID: ${foodId})`
            : "Basic Information"
        }
      >
        <FoodItemBasicInfoEditSection
          foodItem={foodItem}
          setFoodItem={setFoodItem}
        />
      </EditSection>
      <EditSection header="Nutrition Facts">
        <FoodItemNutritionEditSection
          nutritionFacts={nutritionFacts}
          setNutritionFacts={setNutritionFacts}
          supplierId={supplierInfo.supplier_id}
        />
      </EditSection>
      <EditSection header="Tags">
        <FoodItemTagEditSection
          foodItemTags={foodItemTags}
          setFoodItemTags={setFoodItemTags}
          isDish={!!foodItem.portion_size && foodItem.portion_size > 0}
        />
      </EditSection>
      <EditSection header="Supplier Information">
        <FoodSupplierEditSection
          supplierInfo={supplierInfo}
          setSupplierInfo={setSupplierInfo}
          editingFoodId={foodId ? Number(foodId) : undefined}
        />
      </EditSection>
      {editingFoodItemData?.portion_size ? (
        <EditSection header="Sense Checking">
          <FoodItemSenseCheckingSection />
        </EditSection>
      ) : null}
      {editingFoodItemData ? (
        <EditSection>
          <FoodUsageInfo
            usedAsIngredientIn={usedAsIngredientIn}
            usedInMenus={usedInMenus}
            usedInMenuLoops={usedInMenuLoops}
            maxItemsPerSection={5}
          />
        </EditSection>
      ) : null}
      <EditSection>
        <Grid container>
          <Grid item xs={4}>
            <CommonButton text="Save" positive onClick={handleSaveClick} />
          </Grid>
          <Grid item xs={8}>
            <Box display="flex" justifyContent="flex-end">
              <CommonButton text="Save As New" positive onClick={onCreate} />
              {editingFoodItemData ? (
                <>
                  <CommonButton
                    text="Swap"
                    startIcon={<SwapHorizIcon />}
                    disabled={isSwapping}
                    onClick={handleOpenSwapDialog}
                  />
                  <CommonButton
                    text={isActivated ? "Deactivate" : "Activate"}
                    danger={isActivated && !isToggleDisabled}
                    positive={!isActivated && !isToggleDisabled}
                    disabled={isToggleDisabled}
                    onClick={handleToggleActivation}
                  />
                </>
              ) : null}
            </Box>
          </Grid>
        </Grid>
      </EditSection>
      {editingFoodItemData ? (
        <SwapFoodUsagesDialog
          open={isSwapDialogOpen}
          sourceFoodId={editingFoodItemData.id}
          sourceFoodName={editingFoodItemData.food_name ?? ""}
          usedAsIngredientIn={usedAsIngredientIn}
          usedInMenus={usedInMenus}
          usedInMenuLoops={usedInMenuLoops}
          isSwapping={isSwapping}
          errorMessage={swapError}
          report={swapReport}
          onCancel={handleCancelSwap}
          onConfirm={handleConfirmSwap}
          onCloseReport={handleCloseSwapReport}
        />
      ) : null}
    </>
  );
};

export default IngredientEditForms;
